/**
 * Models for the SLEEVE portfolio position linking feature.
 * These map to backend DTOs for managing linked positions between
 * SLEEVE portfolios and their parent STANDARD/VENTURE positions.
 */

/** Fields that can be independently overridden on a linked child position */
export type OverridableField = 'description' | 'shares' | 'costBasis' | 'discount';

/** Fields that always sync from parent and cannot be edited on a linked child position */
export type NonOverridableField = 'positionType' | 'isBoxed' | 'stockType' | 'manualPrice' | 'expiry' | 'strikePrice';

/** Represents a link between a parent position and a child (sleeve) position */
export class PositionLink {
  id: number;
  parentPositionId: number;
  childPositionId: number;
  parentDescription: string;
  parentShares: number;
  parentCostBasis: number;
  parentDiscount: number;
  parentPortfolioName: string;
  parentCompanyName: string;
  descriptionOverride: boolean;
  sharesOverride: boolean;
  costBasisOverride: boolean;
  discountOverride: boolean;
  lastSyncedAt: string;
  lastSyncError: string;
  syncErrorCount: number;
  active: boolean;

  constructor(data: any = {}) {
    this.id = data.id;
    this.parentPositionId = data.parentPositionId;
    this.childPositionId = data.childPositionId;
    this.parentDescription = data.parentDescription;
    this.parentShares = data.parentShares;
    this.parentCostBasis = data.parentCostBasis;
    this.parentDiscount = data.parentDiscount;
    this.parentPortfolioName = data.parentPortfolioName;
    this.parentCompanyName = data.parentCompanyName;
    this.descriptionOverride = data.descriptionOverride || false;
    this.sharesOverride = data.sharesOverride || false;
    this.costBasisOverride = data.costBasisOverride || false;
    this.discountOverride = data.discountOverride || false;
    this.lastSyncedAt = data.lastSyncedAt;
    this.lastSyncError = data.lastSyncError;
    this.syncErrorCount = data.syncErrorCount || 0;
    this.active = data.active ?? true;
  }
}

/** Represents a position that can be linked to from a SLEEVE portfolio */
export class LinkablePosition {
  id: number;
  companyName: string;
  ticker: string;
  portfolioName: string;
  positionType: string;
  stockType: string;
  shares: number;
  costBasis: number;
  description: string;

  constructor(data: any = {}) {
    this.id = data.id;
    this.companyName = data.companyName;
    this.ticker = data.ticker;
    this.portfolioName = data.portfolioName;
    this.positionType = data.positionType;
    this.stockType = data.stockType;
    this.shares = data.shares;
    this.costBasis = data.costBasis;
    this.description = data.description;
  }
}

/** Request body for creating a new position link */
export class CreatePositionLinkRequest {
  parentPositionId: number;

  constructor(parentPositionId: number) {
    this.parentPositionId = parentPositionId;
  }
}

/** Request body for updating override flags on a position link */
export class UpdatePositionLinkOverridesRequest {
  descriptionOverride: boolean;
  sharesOverride: boolean;
  costBasisOverride: boolean;
  discountOverride: boolean;

  constructor(data: any = {}) {
    this.descriptionOverride = data.descriptionOverride || false;
    this.sharesOverride = data.sharesOverride || false;
    this.costBasisOverride = data.costBasisOverride || false;
    this.discountOverride = data.discountOverride || false;
  }
}
